import React, { useRef, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import '../../styles/TMRWMember/2Photobooth.css';

function ConfirmTMRWMember() {
  const location = useLocation();
  const navigate = useNavigate();
  const { images = [], selectedFrame } = location.state || {}; // Get selected frame
  const canvasRef = useRef(null);

  const handleConfirm = () => {
    navigate('/result/TMRWMember', { state: { images, selectedFrame } });
  };

  const handleRetake = () => {
    navigate('/photobooth/TMRWMember', { state: { existingImages: images, selectedFrame } });
  };

  // Implementation with proper image placement within frame photo areas
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Detect mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;
    console.log('Device detection - isMobile:', isMobile, 'userAgent:', navigator.userAgent);
    
    // Helper function to calculate source crop dimensions for an image to fill target area
    const calculateCropDimensions = (imgWidth, imgHeight, targetWidth, targetHeight) => {
      const imgRatio = imgHeight / imgWidth;
      const targetRatio = targetHeight / targetWidth;
      let sourceX, sourceY, sourceWidth, sourceHeight;
      if (imgRatio > targetRatio) {
        // Image is taller than target - crop top and bottom
        sourceWidth = imgWidth;
        sourceHeight = imgWidth * targetRatio;
        sourceX = 0;
        sourceY = (imgHeight - sourceHeight) / 2;
      } else {
        // Image is wider than target - crop left and right
        sourceHeight = imgHeight;
        sourceWidth = imgHeight / targetRatio;
        sourceX = (imgWidth - sourceWidth) / 2;
        sourceY = 0;
      }
      return { sourceX, sourceY, sourceWidth, sourceHeight };
    };
    
    // On mobile, use simpler frame loading approach
    if (isMobile) {
      console.log('Mobile detected, using mobile-optimized layout');
      
      // Load frame first to get proper dimensions
      const frameImg = new window.Image();
      frameImg.src = selectedFrame?.frameImage
      
      frameImg.onload = () => {
        console.log('Mobile frame loaded:', frameImg.src);
        
        // Scale down for mobile but keep reasonable resolution
        const maxWidth = Math.min(600, window.innerWidth - 40);
        const scaleFactor = maxWidth / frameImg.naturalWidth;
        const mobileWidth = Math.round(frameImg.naturalWidth * scaleFactor);
        const mobileHeight = Math.round(frameImg.naturalHeight * scaleFactor);
        
        canvas.width = mobileWidth;
        canvas.height = mobileHeight;
        
        console.log('Mobile canvas dimensions:', canvas.width, 'x', canvas.height);
        
        // Clear canvas
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Define photo areas scaled for mobile
        const photoWidth = mobileWidth * 0.91;
        const photoHeight = photoWidth / (4/3);
        const photoX = (mobileWidth - photoWidth) / 2;
        const topMargin = mobileHeight * 0.055;
        const spacing = mobileHeight * 0.002;
        
        const photoAreas = [
          { x: photoX, y: topMargin, width: photoWidth, height: photoHeight },
          { x: photoX, y: topMargin + (photoHeight + spacing) * 1, width: photoWidth, height: photoHeight },
          { x: photoX, y: topMargin + (photoHeight + spacing) * 2, width: photoWidth, height: photoHeight },
          { x: photoX, y: topMargin + (photoHeight + spacing) * 3, width: photoWidth, height: photoHeight }
        ];
        
        let imagesLoaded = 0;
        const totalImages = images.length;
        
        // Draw photos
        for (let i = 0; i < 4; i++) {
          const imgSrc = i < images.length ? images[i] : null;
          const area = photoAreas[i];
          
          if (imgSrc) {
            const img = new window.Image();
            img.src = imgSrc;
            img.onload = () => {
              const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
                img.naturalWidth,
                img.naturalHeight,
                area.width,
                area.height
              );
              ctx.drawImage(
                img,
                sourceX, sourceY, sourceWidth, sourceHeight,
                area.x, area.y, area.width, area.height
              );
              
              imagesLoaded++;
              if (imagesLoaded === totalImages) {
                // Draw frame on top
                ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
              }
            };
          } else {
            // Draw placeholder
            ctx.fillStyle = '#f0f0f0';
            ctx.fillRect(area.x, area.y, area.width, area.height);
            ctx.fillStyle = '#999';
            ctx.font = `${Math.round(mobileWidth * 0.02)}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText('', area.x + area.width/2, area.y + area.height/2);
          }
        }
        
        // If no images, draw frame immediately
        if (totalImages === 0) {
          ctx.drawImage(frameImg, 0, 0, mobileWidth, mobileHeight);
        }
      };
      
      frameImg.onerror = () => {
        console.error('Mobile frame loading failed, using simple layout');
        // Fallback to simple stacked layout
        const mobileWidth = Math.min(400, window.innerWidth - 40);
        const mobileHeight = Math.round(mobileWidth * 4 / 3);
        canvas.width = mobileWidth;
        canvas.height = mobileHeight * 4;
        
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        for (let i = 0; i < 4; i++) {
          const imgSrc = i < images.length ? images[i] : null;
          const y = i * mobileHeight;
          
          if (imgSrc) {
            const img = new window.Image();
            img.src = imgSrc;
            img.onload = () => {
              ctx.drawImage(img, 0, y, mobileWidth, mobileHeight);
            };
          }
        }
        
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 3;
        ctx.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);
      };
      
      return; // Skip desktop frame loading
    }
    
    // Helper function to draw placeholders
    function drawPlaceholder(area) {
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(area.x, area.y, area.width, area.height);
      ctx.fillStyle = '#999';
      ctx.font = '32px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('', area.x + area.width/2, area.y + area.height/2);
    }
    
    // First, load the frame to get its natural dimensions
    const frameImg = new window.Image();
    // Remove crossOrigin as it might cause issues on mobile
    frameImg.src = selectedFrame?.frameImage
    
    console.log('Attempting to load frame image:', frameImg.src);
    
    // Add timeout for image loading
    const loadTimeout = setTimeout(() => {
      console.error('Frame image loading timed out, falling back to simple layout');
      frameImg.onerror(); // Trigger error handler
    }, 10000); // 10 second timeout
    
    frameImg.onload = () => {
      clearTimeout(loadTimeout);
      console.log('Frame image loaded successfully:', frameImg.src, 'Dimensions:', frameImg.naturalWidth, 'x', frameImg.naturalHeight);
      // Set canvas dimensions to match the frame's natural dimensions
      canvas.width = frameImg.naturalWidth;
      canvas.height = frameImg.naturalHeight;
      
      // Clear canvas with white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Define photo areas within the frame based on the actual frame layout
      // These coordinates are estimated from the frame image you provided
      const frameWidth = frameImg.naturalWidth;
      const frameHeight = frameImg.naturalHeight;
      
      // Calculate photo areas to match the 4:3 aspect ratio used in the booth
      const photoAspectRatio = 4/3; // Same as booth webcam
      const photoWidth = frameWidth * 0.91; // Increased from 0.85 to 0.9 for wider photos
      const photoHeight = photoWidth / photoAspectRatio; // Maintain 4:3 aspect ratio
      const photoX = (frameWidth - photoWidth) / 2; // Center horizontally
      
      // Vertical positioning - evenly spaced photo areas
      const topMargin = frameHeight * 0.055; // Top margin - ADJUST THIS to move all photos up/down
      const spacing = frameHeight * 0.002; // Space between photos - ADJUST THIS to change spacing
      
      const photoAreas = [
        { x: photoX, y: topMargin, width: photoWidth, height: photoHeight },
        { x: photoX, y: topMargin + (photoHeight + spacing) * 1, width: photoWidth, height: photoHeight },
        { x: photoX, y: topMargin + (photoHeight + spacing) * 2, width: photoWidth, height: photoHeight },
        { x: photoX, y: topMargin + (photoHeight + spacing) * 3, width: photoWidth, height: photoHeight }
      ];

      // Track how many images have been loaded
      let imagesLoaded = 0;
      const totalImages = images.length;

      // Function to draw frame after all images are loaded
      const drawFrameOnTop = () => {
        ctx.drawImage(frameImg, 0, 0);
      };

      // Draw photos first, then frame on top
      for (let i = 0; i < 4; i++) {
        const imgSrc = i < images.length ? images[i] : null;
        const area = photoAreas[i];
        
        if (imgSrc) {
          const img = new window.Image();
          img.src = imgSrc;
          img.onload = () => {
            const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
              img.naturalWidth,
              img.naturalHeight,
              area.width,
              area.height
            );
            ctx.drawImage(
              img,
              sourceX, sourceY, sourceWidth, sourceHeight,
              area.x, area.y, area.width, area.height
            );
            
            imagesLoaded++;
            // Draw frame on top after all images are loaded
            if (imagesLoaded === totalImages) {
              drawFrameOnTop();
            }
          };
          img.onerror = () => {
            drawPlaceholder(area);
            imagesLoaded++;
            if (imagesLoaded === totalImages) {
              drawFrameOnTop();
            }
          };
        } else {
          drawPlaceholder(area);
        }
      }

      // If no images to load, draw frame immediately
      if (totalImages === 0) {
        drawFrameOnTop();
      }
    };
    
    frameImg.onerror = () => {
      clearTimeout(loadTimeout);
      console.error('Frame image failed to load at', selectedFrame?.frameImage);
      console.log('Falling back to simple layout');
      
      // Force canvas to be visible with a simple layout
      const fallbackWidth = Math.min(600, window.innerWidth - 40); // Responsive width
      const fallbackHeight = Math.round(fallbackWidth * 4 / 3);
      canvas.width = fallbackWidth;
      canvas.height = fallbackHeight * 4;
      
      console.log('Fallback canvas dimensions:', canvas.width, 'x', canvas.height);
      
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      for (let i = 0; i < 4; i++) {
        const imgSrc = i < images.length ? images[i] : null;
        const y = i * fallbackHeight;
        
        if (imgSrc) {
          const img = new window.Image();
          img.src = imgSrc;
          img.onload = () => {
            const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
              img.naturalWidth,
              img.naturalHeight,
              fallbackWidth,
              fallbackHeight
            );
            ctx.drawImage(
              img,
              sourceX, sourceY, sourceWidth, sourceHeight,
              0, y, fallbackWidth, fallbackHeight
            );
          };
        } else {
          ctx.fillStyle = '#f0f0f0';
          ctx.fillRect(0, y, fallbackWidth, fallbackHeight);
        }
      }
      // Draw a simple border as fallback
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 10;
      ctx.strokeRect(5, 5, canvas.width - 10, canvas.height - 10);
    };
  }, [images, selectedFrame]);

  return (
    <div className="ResultScreen">      
      <div className="content-container">
        <div className="photo-frame-container">
          {images && images.length > 0 ? (
            <canvas 
              ref={canvasRef} 
              className="photo-strip"
              style={{
                maxHeight: '650px',
                maxWidth: '100%',
                width: 'auto',
                height: 'auto',
                border: 'none',
                boxShadow: 'none',
                display: 'block',
                margin: '0 auto',
                background: 'transparent'
              }}
            />
          ) : (
            <p className="no-photos">No photos taken. Please try again.</p>
          )}
        </div>

<div className="right-panel">
  <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>
    Do you want to retake or continue?
  </h2>

  <div 
    className="confirmation-buttons" 
    style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '30px' }}
  >
    <button 
      onClick={handleRetake} 
      className="download-button"
      style={{ backgroundColor: '#000000ff', padding: '15px 30px', fontSize: '18px' }}
    >
      {'>'} Retake 4 more photos
    </button>

    <button 
      onClick={handleConfirm} 
      className="download-button"
      style={{ backgroundColor: '#000000ff', padding: '15px 30px', fontSize: '18px' }}
    >
      {'>'} Create this photostrip
    </button>
  </div>
</div>

      </div>
    </div>
  );
}

export default ConfirmTMRWMember;